import React, { useState, useImperativeHandle, forwardRef } from 'react';
import { FormGroup, Input, Modal, TextArea,Toggle } from 'ps-design';
import ModalBody from 'ps-components/Modal/ModalBody/ModalBody';
import ModalHeader from 'ps-components/Modal/ModalHeader/ModalHeader';
import Select from 'ps-components/ReactSelect/ReactSelect';
import { object, func, string } from 'prop-types';

const scopeValues = [
  {
    value: 'personal',
    label: 'Personal'
  },
  {
    value: 'group',
    label: 'Group'
  },
  {
    value: 'site',
    label: 'Site'
  }
];

const initStates = {
  viewPk: null,
  scope: 'personal',
  viewName: '',
  viewDescription: '',
  isDefault: false,
  permissions: {},
  readOnly: false
};

AddModal.propTypes = {
  addViewModal: object,
  upsertView: func,
  getViews: func,
  masterAccountId: string,
  currentView: object,
  getDashboard: func
};

function AddModal({ addViewModal, upsertView, getViews, masterAccountId, currentView, getDashboard }, ref) {
  const [viewPk, setViewPk] = useState(initStates['viewPk']);
  const [scope, setScope] = useState(initStates['scope']);
  const [viewName, setViewName] = useState(initStates['viewName']);
  const [viewDescription, setViewDescription] = useState(initStates['viewDescription']);
  const [isDefault, setIsDefault] = useState(initStates['isDefault']);
  const [permissions, setPermissions] = useState(initStates['permissions']);
  const [readOnly, setReadOnly] = useState(initStates['readOnly']);

  const scopeList = scopeValues.filter(scope => permissions[scope.value]);

  useImperativeHandle(ref, () => ({
    setStates
  }));

  function handleScopeChange(val) {
    setScope(val.value);
  }

  function handleSetViewName(_id, val) {
    setViewName(val);
  }

  function handleSetViewDescription(_id, val) {
    setViewDescription(val);
  }

  function handleSetDefault() {
    if (readOnly) return;
    setIsDefault(!isDefault);
  }

  function addViewSubmit() {
    upsertView(masterAccountId, viewName, scope, viewDescription, isDefault, viewPk)
      .then(() => getViews(masterAccountId))
      .then(() => {
        if (currentView.view_pk === viewPk) getDashboard(masterAccountId);
      })
      .then(() => addViewModal.current?.hide());
  }

  function setStates(viewPk, scope, name, description, newDefault, permissions, isReadOnly) {
    setViewPk(viewPk ? viewPk : initStates['viewPk']);
    setScope(scope ? scope : initStates['scope']);
    setViewName(name ? name : initStates['viewName']);
    setViewDescription(description ? description : initStates['viewDescription']);
    setIsDefault(!!newDefault);
    setPermissions(permissions ? permissions : initStates['permissions']);
    setReadOnly(isReadOnly ? isReadOnly : initStates['readOnly']);
  }

  function resetStates() {
    setViewPk(initStates['viewPk']);
    setScope(initStates['scope']);
    setViewName(initStates['viewName']);
    setViewDescription(initStates['viewDescription']);
    setIsDefault(initStates['isDefault']);
    setPermissions(initStates['permissions']);
    setReadOnly(initStates['readOnly']);
  }

  return (
    <Modal
      ref={addViewModal}
      onClose={resetStates}
      className="dashboard-grid__add-modal-container"
      id="close"
      displayCloseButton={true}
    >
      <ModalHeader className="dashboard-grid__add-views-header">
        {viewPk ? 'Update Dashboard' : 'Add Dashboard'}
      </ModalHeader>
      <ModalBody className="dashboard-grid__add-views-body">
        <div className="dashboard-grid__body-content">
          <div className="left">
            <FormGroup text="Name">
              <Input
                className="dashboard-grid__add-views-input"
                autoComplete="off"
                id="dashboard-grid__add-views-name-input"
                name="dashboard-grid__add-views-name-input"
                onChange={handleSetViewName}
                placeholder="View Name"
                value={viewName}
                disabled={readOnly}
              />
            </FormGroup>
            <FormGroup text="Scope">
              <Select
                id="dashboard-grid__add-views-scope-input"
                className="dashboard-grid__add-views-input"
                options={scopeList}
                value={scopeList.find(option => option.value === scope)}
                onChange={handleScopeChange}
                isDisabled={readOnly}
              />
            </FormGroup>
          </div>

          <div className="right">
            <FormGroup text="Description">
              <TextArea
                id="dashboard-grid__add-views-description-input"
                name="dashboard-grid__add-views-description-input"
                className="dashboard-grid__add-views-input"
                onChange={handleSetViewDescription}
                placeholder="View Description"
                value={viewDescription}
                disabled={readOnly}
              />
            </FormGroup>
          </div>
        </div>

        <div className="dashboard-grid__add-views-button-container">
          <div className="dashboard-grid__add-views-default">
            <div className="toggle-label">Set as default?</div>
            <Toggle
              onChange={handleSetDefault}
              value={isDefault}
              id="dashboard-grid__add-views-default-toggle"
              className="dashboard-grid__add-views-default-toggle"
            />
          </div>
          {!readOnly && (
            <button
              className="pxl-btn pxl-btn--primary dashboard-grid__add-views-button"
              onClick={addViewSubmit}
              tabIndex="0"
            >
              {viewPk ? 'Update Dashboard' : 'Add Dashboard'}
            </button>
          )}
        </div>
      </ModalBody>
    </Modal>
  );
}

export default forwardRef(AddModal);
